/**
 * Pure logic of the field app's offline queue (no browser APIs), shared by the IndexedDB-backed queue and its unit
 * tests (backend/tests/fieldsync.test.js imports this file directly).
 *
 * A queued op:
 *   { clientId, seq, type, params, payload, meta, createdAt, status, lastError, attempts, summary, link, force }
 * status: 'pending' (waiting to sync) | 'conflict' (server state changed - user decides) | 'failed' (not accepted)
 * Applied ops are removed from the queue.
 */

export const OP_LABELS = {
  'audit.scan': 'Audit scan',
  'audit.signoff': 'Room sign-off',
  'blindcount.submit': 'Blind count',
  'firstwalk.bind': 'Tag binding',
  'firstwalk.exception': 'First-walk exception',
  'custody.return': 'Custody return',
  'custody.move': 'Move',
  'asset.condition': 'Recondition',
  'damage.report': 'Damage report',
};

let counter = 0;
/** Unique, sortable id generated on the device when the action is taken (the server's idempotency key). */
export function newClientId(now = Date.now()) {
  counter = (counter + 1) % 1296;
  return `${now.toString(36)}-${counter.toString(36).padStart(2, '0')}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Builds a queued op. `seq` keeps the order the actions were taken in, even within the same millisecond. */
export function makeOp({ type, params, payload, meta, summary, link, clientId }, now = Date.now(), lastSeq = 0) {
  if (!OP_LABELS[type]) throw new Error(`Unknown field action ${type}`);
  return {
    clientId: clientId || newClientId(now),
    seq: Math.max(now * 1000, lastSeq + 1),
    type,
    params: params || {},
    payload: payload || {},
    ...(meta ? { meta } : {}),
    createdAt: new Date(now).toISOString(),
    status: 'pending',
    lastError: null,
    attempts: 0,
    summary: summary || OP_LABELS[type],
    ...(link ? { link } : {}),
  };
}

export const byOrder = (a, b) => a.seq - b.seq || String(a.clientId).localeCompare(String(b.clientId));
export const pendingOf = (ops) => ops.filter((o) => o.status === 'pending').sort(byOrder);
export const attentionOf = (ops) => ops.filter((o) => o.status === 'conflict' || o.status === 'failed').sort(byOrder);

/** The op as sent to POST /api/field-sync (local-only fields stay on the device). */
export function toWire(op) {
  return {
    clientId: op.clientId,
    type: op.type,
    ...(op.params && Object.keys(op.params).length ? { params: op.params } : {}),
    ...(op.payload && Object.keys(op.payload).length ? { payload: op.payload } : {}),
    ...(op.meta ? { meta: op.meta } : {}),
    createdAt: op.createdAt,
    ...(op.force ? { force: true } : {}),
  };
}

/**
 * Applies a /api/field-sync response to the queue.
 * Returns { remove: clientId[], update: op[] } - applied / duplicate ops leave the queue; conflicts and rejections stay
 * for the user with the server's reason; transient failures stay pending with the reason and one more attempt counted.
 */
export function applyResults(ops, response, now = Date.now()) {
  const byId = new Map(ops.map((o) => [o.clientId, o]));
  const remove = [];
  const update = [];
  for (const r of response?.results || []) {
    const op = byId.get(r.clientId);
    if (!op) continue;
    const at = new Date(now).toISOString();
    if (r.outcome === 'applied' || r.outcome === 'duplicate') remove.push(op.clientId);
    else if (r.outcome === 'conflict') update.push({ ...op, status: 'conflict', lastError: r.message || 'The record changed while you were offline', httpStatus: r.status, attempts: op.attempts + 1, lastAttemptAt: at, force: false });
    else if (r.outcome === 'rejected') update.push({ ...op, status: 'failed', lastError: r.message || 'Not accepted', httpStatus: r.status, attempts: op.attempts + 1, lastAttemptAt: at, force: false });
    else update.push({ ...op, status: 'pending', lastError: r.message || 'Will retry', attempts: op.attempts + 1, lastAttemptAt: at });
  }
  return { remove, update };
}

/** Retry from the Conflicts list: back in the queue, keeping its place, and allowed past the device-side staleness check. */
export const retried = (op) => ({ ...op, status: 'pending', force: true, lastError: null });

/**
 * Migrates the audit page's former localStorage queue (audit_walk_queue_v1) into field ops, keeping the order.
 * Legacy entries: { type: 'scan'|'signoff', clientId, batchId, roomId, tag, condition, method, deviceId, photoUrl, scannedAt }
 */
export function migrateLegacyAuditQueue(legacy, now = Date.now()) {
  if (!Array.isArray(legacy)) return [];
  let seq = now * 1000;
  return legacy
    .filter((o) => o && (o.type === 'scan' || o.type === 'signoff') && o.batchId && o.roomId)
    .map((o) => {
      seq += 1;
      const base = { clientId: String(o.clientId || newClientId(now)), seq, createdAt: o.scannedAt || new Date(now).toISOString(), status: 'pending', lastError: null, attempts: 0 };
      if (o.type === 'signoff') {
        return { ...base, type: 'audit.signoff', params: { batchId: Number(o.batchId), roomId: Number(o.roomId) }, payload: {}, summary: 'Room sign-off', link: `/physical-audit-verification?batchId=${o.batchId}&roomId=${o.roomId}` };
      }
      const payload = { roomId: Number(o.roomId), tag: o.tag };
      ['condition', 'method', 'deviceId', 'photoUrl', 'scannedAt', 'notes'].forEach((k) => o[k] !== undefined && (payload[k] = o[k]));
      return { ...base, type: 'audit.scan', params: { batchId: Number(o.batchId) }, payload, summary: `Audit scan ${o.tag}`, link: `/physical-audit-verification?batchId=${o.batchId}&roomId=${o.roomId}` };
    });
}

const norm = (s) => String(s || '').trim().toLowerCase();
const compact = (s) => norm(s).replace(/[\s-]/g, '');

/** True when a scanned tag matches a checklist item (asset tag, or the printed short code with or without dashes). */
export const tagMatches = (item, tag) => norm(item.assetTag) === norm(tag) || (item.shortCode && compact(item.shortCode) === compact(tag));

/**
 * Shows queued (not yet synced) audit scans on a cached checklist, so a room reopened offline keeps its progress.
 * Only items expected in the room are ticked; anything else is resolved by the server on sync.
 */
export function overlayChecklist(checklist, ops, batchId, roomId) {
  if (!checklist?.items) return checklist;
  const scans = pendingOf(ops).filter((o) => o.type === 'audit.scan' && Number(o.params?.batchId) === Number(batchId) && Number(o.payload?.roomId) === Number(roomId));
  if (!scans.length) return checklist;
  const items = checklist.items.map((i) => {
    if (i.expectedLocationId !== checklist.room?.id) return i;
    const hit = scans.filter((o) => tagMatches(i, o.payload.tag)).pop();
    return hit ? { ...i, status: 'verified', condition: hit.payload.condition || i.condition, verifiedAt: hit.payload.scannedAt || hit.createdAt, queued: true } : i;
  });
  return { ...checklist, items };
}

/** Counts for badges and banners. */
export function summarize(ops) {
  const pending = pendingOf(ops);
  const byType = {};
  pending.forEach((o) => (byType[o.type] = (byType[o.type] || 0) + 1));
  return { pending: pending.length, conflicts: ops.filter((o) => o.status === 'conflict').length, failed: ops.filter((o) => o.status === 'failed').length, byType };
}

/** Human size of a byte count. */
export function formatBytes(n) {
  if (!n) return '0 KB';
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
